import React, { Component } from "react";
import PropTypes from "prop-types";

import i18n from "i18n-react";
import { spring, StaggeredMotion } from "react-motion";
import { withRouter } from "react-router-dom";

import clsx from "clsx";
import {
  APIHandler,
  DataHandler,
  StorageHandler,
  TrainingCard,
  SubAppButton,
  ContentHeader,
  FastTrack,
} from "C4";
import { storageKeys, baseAppId, hasFastTrack, fastTrackId } from "settings";

const fastTrackBrackets = [
  { grade: "A+", range: 97, n: true },
  { grade: "A", range: 93, n: true },
  { grade: "A-", range: 90, n: true },
  { grade: "B+", range: 87 },
  { grade: "B", range: 83 },
  { grade: "B-", range: 80 },
  { grade: "C+", range: 77 },
  { grade: "C", range: 73 },
  { grade: "C-", range: 70 },
  { grade: "D+", range: 67 },
  { grade: "D", range: 63 },
  { grade: "D-", range: 60 },
  { grade: "F", range: 0, n: true },
];

const fastTrackData = (key, value = null) => {
  const data = JSON.parse(localStorage.getItem("fastTrackData")) || {};
  if (key && value === null) {
    if (data) return data[key];
    return null;
  }
  if (key && value) {
    data[key] = value;
    return localStorage.setItem("fastTrackData", JSON.stringify(data));
  }
  throw Error("Invalid input");
};

const setFastTrackGrade = async () => {
  const appId = await DataHandler.matchAppId();
  const { response: user } =
    (await StorageHandler.load(storageKeys.user)) || {};
  if (user) {
    const { email } = user;
    const startTime = fastTrackData("startTime");
    const total = Math.floor((Date.now() - (startTime || 0)) / 1000);
    await APIHandler.updateUser(appId, email, [
      {
        param: "fast_track_score",
        val:
          (fastTrackData("correctAnswers").length() /
            fastTrackData("totalQuestions")) *
          100,
      },
      { param: "fast_track_duration", val: total },
    ]);
  }
};

class TrainingGrid extends Component {
  constructor(props) {
    super(props);

    const fastTrackFinished = fastTrackData("finished") || false;
    const fastTrackErrors = +fastTrackData("errors") || 0;

    this.state = {
      appId: 0,
      chapters: [],
      chapters_progress: [],
      subApps: [],
      fastTrackCanceled: fastTrackData("canceled") || false,
      fastTrackErrors,
      fastTrackFinished,
      fastTrackGrade: { grade: "", n: "" },
    };

    this.isLocked = this.isLocked.bind(this);
    this.loadChapters = this.loadChapters.bind(this);
    this.calculateProgress = this.calculateProgress.bind(this);
  }

  componentDidMount() {
    const { fastTrackErrors, fastTrackFinished } = this.state;

    this.mounted = true;

    this.getProgress();

    if (fastTrackFinished && fastTrackErrors !== -1) {
      this.getGrade(fastTrackErrors);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getProgress() {
    StorageHandler.load(storageKeys.user).then((res) => {
      if (res) {
        const { email } = res;
        const { appId } = window;
        if (this.mounted) {
          this.setState({ appId });
          APIHandler.getUserProgress(appId, email).then((userProgress) => {
            this.calculateProgress(userProgress);
          });
        }
      }
    });
  }

  async getGrade() {
    const appId = await DataHandler.matchAppId();
    const pages = await APIHandler.getAppQuestions(fastTrackId);
    const correctAnswers = [...new Set(fastTrackData("correctAnswers"))];
    const percentage = (correctAnswers.length / pages.length) * 100;
    const percentage2 = String(Math.round(percentage));

    const [letterGrade] = fastTrackBrackets
      .map(({ grade, range, n }) => {
        if (percentage >= range) {
          return { grade: percentage2, range: n === true ? "n" : "" };
        }
        return false;
      })
      .filter((val) => val !== false);
    this.setState({ fastTrackGrade: letterGrade });

    const { response: user } = await StorageHandler.load(storageKeys.user);
    if (user) {
      const { email } = user;
      const startTime = fastTrackData("startTime");
      const total = Math.floor((Date.now() - (startTime || 0)) / 1000);
      await APIHandler.updateUser(appId, email, [
        { param: "fast_track_duration", val: total },
        { param: "fast_track_score", val: Math.floor(percentage) },
      ]);
    }
  }

  getSubAppProgress(appIds) {
    return StorageHandler.load(storageKeys.user).then((res) => {
      if (res) {
        const { email } = res;
        if (this.mounted && appIds.length > 0) {
          return APIHandler.getUserProgress(appIds, email).then(
            (userProgress) => {
              const progress = Object.values(userProgress.data).map((app) => {
                const [chapter] = app.chapters;
                const { name, app_id } = app;
                return {
                  chapter,
                  name,
                  app_id: parseInt(app_id),
                };
              });
              return progress;
            }
          );
        }
      }
      return null;
    });
  }

  async setSubApps(appId) {
    const { chapters_progress } = this.state;
    const user = await StorageHandler.load(storageKeys.user);
    if (user) {
      const { email } = user;

      const trainings = await APIHandler.getSubApps(appId);
      trainings.sort((a, b) => a.number - b.number);

      const appIds = trainings.map(({ sessions }) =>
        sessions.map((item) => item.session_app_id)
      );
      const allAppIds = appIds.flat();
      const progress = await this.getSubAppProgress(allAppIds);

      const subAppPromises = trainings.map(async (training) => {
        const [mainSession] = training.sessions;
        if (!mainSession) return null;

        const {
          session_app_id: sessionAppId,
          title,
          background_image: backgroundImage,
          sub_app: subApp,
        } = mainSession;

        const userProgress = progress.find(
          ({ app_id }) => app_id === sessionAppId
        );
        console.log(progress, sessionAppId);

        if (!userProgress) return null;

        const {
          chapter,
          chapter: {
            last_page: lastPage,
            total_pages: totalPages,
            chapter: chapterId,
          },
        } = userProgress;

        this.setState({ chapters_progress: [...chapters_progress, chapter] });

        const pageId = lastPage < totalPages ? lastPage : 0;
        const percentage = Math.round(
          (chapter.progress / chapter.total_pages) * 100
        );
        return (
          <SubAppButton
            key={chapterId}
            appId={sessionAppId}
            progress={chapter}
            name={title}
            pageId={pageId}
            size={{ height: 100, width: 100 }}
            type="training"
            email={email}
            chapterId={chapterId}
            backgroundImage={backgroundImage}
            locked={this.isLocked(chapters_progress.length)}
          />
        );
      });

      if (this.mounted) {
        const subApps = (await Promise.all(subAppPromises)).filter(Boolean);
        this.setState({ subApps });
      }
      return null;
    }

    this.setState({ subApps: [] });
    return null;
  }

  loadChapters(setProgress) {
    const { appId } = this.state;
    DataHandler.checkKey(
      storageKeys.chapters,
      APIHandler.getChapters(appId),
      true
    ).then((res) => {
      const startingValues = [];
      Object.keys(res.app).map(() => {
        const newObj = {};
        newObj.data = 0;
        newObj.pos = 20;
        return startingValues.push(newObj);
      });
      if (!setProgress) {
        StorageHandler.load(storageKeys.user).then((user) => {
          APIHandler.registerUserProgress(user.email).then((registered) => {
            if (registered) {
              this.getProgress();
            }
          });
        });
      } else {
        const chapters = (
          <StaggeredMotion
            defaultStyles={startingValues}
            styles={(prevInterpolatedStyles) =>
              prevInterpolatedStyles.map((_, i) =>
                i === 0
                  ? {
                      data: spring(1, { stiffness: 320, damping: 28 }),
                      pos: spring(0, { stiffness: 320, damping: 28 }),
                    }
                  : {
                      data: spring(prevInterpolatedStyles[i - 1].data, {
                        stiffness: 320,
                        damping: 28,
                      }),
                      pos: spring(prevInterpolatedStyles[i - 1].pos, {
                        stiffness: 320,
                        damping: 28,
                      }),
                    }
              )
            }
          >
            {(styles) => (
              <React.Fragment>
                {res.app.map((chapter, key) => (
                  <React.Fragment>
                    {key === 0 && (
                      <span className="subHeader">
                        <ContentHeader
                          header={i18n.translate(
                            "contentHeaders.maintrainings"
                          )}
                          subtitle={i18n.translate(
                            "pageDescriptions.maintrainings"
                          )}
                          line
                        />
                      </span>
                    )}
                    {key === 4 && (
                      <span className="subHeader">
                        <ContentHeader
                          header={i18n.translate("contentHeaders.subtrainings")}
                          subtitle={i18n.translate(
                            "pageDescriptions.subtrainings"
                          )}
                          line
                        />
                      </span>
                    )}
                    <div
                      key={key}
                      className={clsx("trainingCard__Animated", {
                        specialization: key >= 4,
                      })}
                      style={{
                        opacity: `${styles[key].data}`,
                        transform: `translateY(${styles[key].pos}%)`,
                      }}
                    >
                      <TrainingCard
                        chaptersProgress={this.state.chapters_progress[key]}
                        chapter={chapter}
                        chapterCount={chapter.chapterItemCount}
                        chapterCountMin={chapter.chapterItemCountMin}
                        locked={this.isLocked(key)}
                        index={key}
                        appId={appId}
                        key={chapter.chapterID}
                      />
                    </div>
                  </React.Fragment>
                ))}
              </React.Fragment>
            )}
          </StaggeredMotion>
        );
        this.setState({ chapters });
      }
    });
  }

  calculateProgress(res) {
    if (res.status !== "error") {
      const { chapters } = res.data[0];
      chapters.forEach((chapter) => {
        const newChapter = chapter;
        if (chapter.progress > 0 && chapter.total_pages > 0) {
          newChapter.percentage = Math.floor(
            (chapter.progress / chapter.total_pages) * 100
          );
          if (newChapter.percentage > 100) {
            newChapter.percentage = 100;
          }
          // Bug fix for last_page higher than progress
          if (
            Math.floor((chapter.total_pages / chapter.last_page) * 100) === 100
          ) {
            newChapter.percentage = 100;
          }
        }
      });
      this.setState({ chapters_progress: chapters }, () => {
        this.loadChapters(res.status !== "error");
        this.setSubApps(baseAppId);
      });
    }
    this.loadChapters(res.status !== "error");
    this.setSubApps(baseAppId);
  }

  isLocked(key) {
    const { chapters_progress: progress } = this.state;
    // If specialization chapter, lock if not all foundation chapters are completed, otherwise unlock
    if (!!progress[key].specialization === true) {
      const foundationChapters = progress.filter(
        (chapter) => !!chapter.specialization === false
      );
      const foundationChaptersCompleted = foundationChapters.filter(
        (chapter) => chapter.percentage >= 100
      );
      if (foundationChaptersCompleted.length === foundationChapters.length) {
        return false;
      }
      return true;
    }
    // Lock chapter if it's not the first chapter and the previous chapter isn't completed
    if (
      progress.length > 0 &&
      key !== 0 &&
      progress[key - 1].percentage !== 100
    ) {
      return true;
    }
    // Unlock chapter if it's the first chapter or the previous chapter is completed
    if (
      progress.length > 0 &&
      (key === 0 || progress[key - 1].percentage >= 100)
    ) {
      return false;
    }
    return true;
  }

  render() {
    const { history } = this.props;
    const {
      chapters,
      subApps,
      fastTrackFinished,
      fastTrackErrors,
      fastTrackCanceled,
      fastTrackGrade,
    } = this.state;

    return (
      <div>
        {chapters && subApps && (
          <div>
            <div className="trainingGrid">{chapters}</div>
            {subApps.length > 0 && (
              <div className="subApps__Container">
                <ContentHeader
                  header={i18n.translate("contentHeaders.subapps")}
                  subtitle={i18n.translate("pageDescriptions.subtrainings")}
                />
                <div className="trainingGrid subApps">{subApps}</div>
              </div>
            )}
          </div>
        )}
        <FastTrack
          fastTrackCanceled={fastTrackCanceled}
          fastTrackErrors={+fastTrackErrors}
          fastTrackFinished={fastTrackFinished}
          fastTrackGrade={fastTrackGrade}
          hasFastTrack={hasFastTrack}
          onConfirmAsk={() => {
            this.setState({ fastTrackErrors: "0" }, () => {
              history.push("/main/fasttrack/chapter/0/0");
            });
          }}
          onCancelAsk={() => {
            fastTrackData("canceled", true);
            this.setState({ fastTrackCanceled: true });
          }}
          onConfirmGrade={() => {
            const { grade } = fastTrackGrade || {};
            if (grade.length > 0) {
              setFastTrackGrade(0);
            }
            fastTrackData("errors", -1);
            fastTrackData("grade", grade);
            this.setState({ fastTrackFinished: true, fastTrackErrors: -1 });
          }}
        />
      </div>
    );
  }
}

TrainingGrid.propTypes = {
  history: PropTypes.shape().isRequired,
};

export default withRouter(TrainingGrid);
